import { useUser, useClerk, useAuth, useSession } from '#imports'
import { ref, computed, watch, onMounted, useRuntimeConfig } from '#imports'
import { defineStore } from 'pinia'
import authService from '../utils/authService'
import { musicService } from '~/services/musicService'
import { api } from '~/utils/api'
import { trackEvent } from '~/utils/analytics'

// 本地存储键名
const ACCOUNT_CACHE_KEY = 'user_account_cache'
const LOGIN_CONTEXT_KEY = 'musicmint_login_context'
const REFRESH_INTERVAL = 30000
const MIN_REFRESH_INTERVAL = 5000
const BACKEND_ACCOUNT_401_BACKOFF = 2 * 60 * 1000

let globalRefreshTimer: ReturnType<typeof setInterval> | null = null
let lastRefreshTime = 0
let backendAccountUnauthorizedUntil = 0
let localBackendClerkSyncPromise: Promise<boolean> | null = null

const isLocalApiBaseUrl = (baseUrl: string) => {
  if (!baseUrl) return false

  try {
    const parsed = new URL(baseUrl)
    return ['localhost', '127.0.0.1', '::1', '[::1]'].includes(parsed.hostname)
  } catch {
    return false
  }
}

const buildLocalClerkUserCreatedPayload = (clerkUser: any) => {
  if (!clerkUser?.id) return null

  const primaryEmail =
    clerkUser?.primaryEmailAddress?.emailAddress ||
    clerkUser?.emailAddresses?.[0]?.emailAddress ||
    ''

  return {
    type: 'user.created',
    object: 'event',
    timestamp: Date.now(),
    instance_id: 'musicmint-local-dev',
    data: {
      id: clerkUser.id,
      first_name: clerkUser.firstName || '',
      last_name: clerkUser.lastName || '',
      username: clerkUser.username || '',
      email_addresses: primaryEmail
        ? [{ email_address: primaryEmail }]
        : [],
    },
    event_attributes: {
      http_request: {
        client_ip: '127.0.0.1',
        user_agent:
          typeof navigator !== 'undefined'
            ? navigator.userAgent
            : 'MusicMint Local Dev',
      },
    },
  }
}

const getReferrerDomain = (referrer: string) => {
  if (!referrer) return 'direct'
  try {
    return new URL(referrer).hostname
  } catch {
    return referrer.slice(0, 255)
  }
}

const buildLoginContext = () => {
  if (typeof window === 'undefined') return null

  const searchParams = new URLSearchParams(window.location.search)
  const referrer = document.referrer || ''

  return {
    auth_provider: 'clerk',
    page_path: `${window.location.pathname}${window.location.search}`,
    referrer_domain: getReferrerDomain(referrer),
    utm_source: searchParams.get('utm_source') || '',
    utm_medium: searchParams.get('utm_medium') || '',
    utm_campaign: searchParams.get('utm_campaign') || '',
  }
}

const readStoredLoginContext = () => {
  if (typeof window === 'undefined') return null

  try {
    const raw = localStorage.getItem(LOGIN_CONTEXT_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

// 用户账户接口定义
export interface UserAccount {
  id: string
  user_id: string
  type: number
  vip_level: number
  free_points: number
  vip_points: number
  buy_points: number
  has_bought: number
  token: string
  productId: string
  inapp_product_id: string
}

// 定义 Pinia Store 类型
export interface AccountState {
  userAccount: UserAccount | null
  isLoadingAccount: boolean
  isLoggingIn: boolean
  autoFetchingToken: boolean
  isUpdating: boolean
  isInitialized: boolean
  isRefreshing: boolean  // 新增刷新状态
}

// 定义 Pinia Store
export const useAccountStore = defineStore('account', {
  state: (): AccountState => ({
    userAccount: null,
    isLoadingAccount: false,
    isLoggingIn: false,
    autoFetchingToken: false,
    isUpdating: false,
    isInitialized: false,
    isRefreshing: false,  // 新增刷新状态
  }),
  
  getters: {
    // 计算总积分
    totalPoints(): number {
      if (!this.userAccount) return 0
      return this.userAccount.free_points + 
             this.userAccount.vip_points + 
             this.userAccount.buy_points
    }
  },
  
  actions: {
    // 设置用户账户
    setUserAccount(account: UserAccount | null) {
      this.userAccount = account
      // 同步更新缓存
      if (typeof window !== 'undefined') {
        if (account) {
          localStorage.setItem(ACCOUNT_CACHE_KEY, JSON.stringify(account))
        } else {
          localStorage.removeItem(ACCOUNT_CACHE_KEY)
        }
      }
    },
    
    // 加载缓存的用户账户 - 仅用于快速显示，不替代API请求
    loadCachedAccount() {
      if (typeof window === 'undefined') return false
      
      try {
        const cached = localStorage.getItem(ACCOUNT_CACHE_KEY)
        if (cached) {
          const parsedData = JSON.parse(cached)
          // 简单验证数据结构
          if (parsedData && parsedData.id && parsedData.user_id) {
            // No direct setUserAccount here, let init flow decide based on Clerk state
            this.userAccount = parsedData; // Temporarily set for init check
            return true // 返回true表示成功加载了缓存
          }
        }
      } catch (error) {
        console.error('加载缓存的账户信息失败:', error)
        localStorage.removeItem(ACCOUNT_CACHE_KEY)
      }
      
      return false // 返回false表示没有加载到缓存
    },
    
    // 设置加载状态
    setLoadingAccount(isLoading: boolean) {
      this.isLoadingAccount = isLoading
    },
    
    // 设置登录中状态
    setLoggingIn(isLoggingIn: boolean) {
      this.isLoggingIn = isLoggingIn
    },
    
    // 设置获取token状态
    setAutoFetchingToken(isFetching: boolean) {
      this.autoFetchingToken = isFetching
    },
    
    // 清除用户账户
    clearUserAccount() {
      this.setUserAccount(null)
    },
    
    // 设置初始化状态
    setInitialized(isInitialized: boolean) {
      this.isInitialized = isInitialized
    },
    
    // 设置更新状态
    setUpdating(isUpdating: boolean) {
      this.isUpdating = isUpdating
    },
    
    // 设置刷新状态
    setRefreshing(isRefreshing: boolean) {
      this.isRefreshing = isRefreshing
    },
  }
})

// 全局用户账户状态管理
export function useAuthState() {
  const { isLoaded: userLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const clerk = useClerk();
  const { isLoaded: sessionLoaded } = useSession();
  const runtimeConfig = useRuntimeConfig();

  // 使用Pinia Store
  const accountStore = useAccountStore();

  const isAuthReady = computed(() => userLoaded.value === true && sessionLoaded.value === true);

  // Wait for Clerk user and session state before treating the user as signed in or signed out.
  const isAuthenticated = computed(() => isAuthReady.value && isSignedIn.value === true);

  const syncLocalBackendClerkUser = async () => {
    const apiBaseUrl = String(runtimeConfig.public.apiBaseUrl || '')
    if (!isLocalApiBaseUrl(apiBaseUrl)) return false

    const payload = buildLocalClerkUserCreatedPayload(user.value)
    if (!payload) return false

    if (localBackendClerkSyncPromise) {
      return localBackendClerkSyncPromise
    }

    localBackendClerkSyncPromise = (async () => {
      try {
        const response = await api.post<any>('music/public/v1/clerk/webhook', payload, {
          timeout: 10000,
        })
        const ok = response?.code === 200
        if (ok) {
          console.log('syncLocalBackendClerkUser: Synced Clerk user to local backend.')
        } else {
          console.warn(
            'syncLocalBackendClerkUser: Local backend sync returned unexpected response.',
            response
          )
        }
        return ok
      } catch (error) {
        console.warn(
          'syncLocalBackendClerkUser: Failed to sync Clerk user to local backend.',
          error
        )
        return false
      } finally {
        localBackendClerkSyncPromise = null
      }
    })()

    return localBackendClerkSyncPromise
  }

  // 处理登录
  const handleSignIn = async () => {
    try {
      // 设置登录中状态
      accountStore.setLoggingIn(true);
      const loginContext = buildLoginContext();
      if (loginContext && typeof window !== 'undefined') {
        localStorage.setItem(LOGIN_CONTEXT_KEY, JSON.stringify(loginContext));
        trackEvent('login_start', loginContext);
      }
      // console.log('window.location.href',window.location)
      // 使用clerk实例直接调用
      const signInInstance = await clerk.value?.openSignIn({
        fallbackRedirectUrl: window.location.href,
      });

      // 监听登录状态变化，当isSignedIn发生变化时表示登录成功
      // signInInstance is void, watch isSignedIn for login success
      const unwatchSignIn = watch(isSignedIn, (newValue) => {
        if (newValue === true) {
          const storedLoginContext = readStoredLoginContext();
          if (storedLoginContext && typeof window !== 'undefined') {
            trackEvent('login_success', {
              ...storedLoginContext,
              page_path_after_login: `${window.location.pathname}${window.location.search}`,
            });
            localStorage.removeItem(LOGIN_CONTEXT_KEY);
          }
          accountStore.setLoggingIn(false);
          unwatchSignIn(); // 清理监听
          // 登录成功后，initAuthStateWatcher 中的 isAuthenticated watch 会处理后续
          // fetchUserAccount(); // Redundant if watcher handles it
        }
      });

      // 简单的超时机制，避免状态卡住
      // 如果用户关闭弹窗但未登录，或者登录过程被中断，会自动重置状态
      setTimeout(() => {
        if (accountStore.isLoggingIn) {
          accountStore.setLoggingIn(false);
          unwatchSignIn(); // 清理监听
        }
      }, 5000); // Increased timeout slightly // 设置合理的超时时间

      return true;
    } catch (error) {
      console.error("Sign in failed:", error);
      accountStore.setLoggingIn(false);
      return false;
    }
  };

  // 获取并存储token
  const fetchAndStoreToken = async () => {
    // Removed autoFetchingToken check here, let the store handle its state if needed
    // if (accountStore.autoFetchingToken) {
    //   return authService.getToken(); // Return current token if already fetching
    // }
    try {
      accountStore.setAutoFetchingToken(true);
      if (!isAuthReady.value) {
        return authService.getToken();
      }

      if (getToken.value && isSignedIn.value) { // Ensure user is signed in before getToken
        const jwt = await getToken.value({ template: "supabase" });
        if (jwt) {
          authService.saveToken(jwt);
          return jwt;
        } else {
          // If getToken returns null/undefined while isSignedIn is true, clear local token.
          authService.clearToken();
        }
      } else {
         // Not signed in or no getToken function, clear local token
        authService.clearToken();
      }
      return null;
    } catch (error) {
      console.error("获取token失败:", error);
      authService.clearToken(); // Clear token on error
      return null;
    } finally {
      accountStore.setAutoFetchingToken(false);
    }
  };

  // 清除token
  const clearToken = () => {
    authService.clearToken();
  };

  // 处理登出
  const handleLogout = async () => {
    try {
      clearToken();
      accountStore.clearUserAccount();
      saveUserAccountToCookie(null);
      stopGlobalRefresh(); // Stop refresh timers first
      await clerk.value?.signOut(); // This should trigger isSignedIn watcher
      console.log("Logout process completed.");
      return true;
    } catch (error) {
      console.error("登出失败:", error);
      return false;
    }
  };

  // 获取用户账户信息
  const fetchUserAccount = async () => {
    accountStore.setLoadingAccount(true);

    if (!isAuthReady.value) {
      console.log("fetchUserAccount: Clerk auth state is not loaded yet. Keeping local auth state unchanged.");
      accountStore.setLoadingAccount(false);
      accountStore.setRefreshing(false);
      return accountStore.userAccount;
    }

    if (!isSignedIn.value) {
      console.log("fetchUserAccount: Clerk user not signed in. Ensuring local state is clear.");
      backendAccountUnauthorizedUntil = 0;
      if (accountStore.userAccount) accountStore.clearUserAccount();
      if (authService.getToken()) authService.clearToken();
      saveUserAccountToCookie(null);
      stopGlobalRefresh();
      accountStore.setLoadingAccount(false);
      accountStore.setRefreshing(false);
      return null;
    }

    const now = Date.now();
    if (backendAccountUnauthorizedUntil > now) {
      console.log(`fetchUserAccount: Backend account is unauthorized, skipping account fetch for ${Math.ceil((backendAccountUnauthorizedUntil - now) / 1000)}s.`);
      accountStore.setLoadingAccount(false);
      accountStore.setRefreshing(false);
      return accountStore.userAccount;
    }

    const token = await fetchAndStoreToken();
    if (!token) {
      console.warn("fetchUserAccount: Failed to fetch/refresh token from Clerk (isSignedIn is true). Aborting account fetch.");
      // This is a critical issue. If signed in but can't get token, might need to force a logout.
      // await handleLogout(); // Consider this if the state persists
      accountStore.setLoadingAccount(false);
      accountStore.setRefreshing(false);
      return null;
    }

    if (accountStore.isUpdating) {
      console.log("fetchUserAccount: An update is already in progress.");
      accountStore.setLoadingAccount(false);
      accountStore.setRefreshing(false);
      return accountStore.userAccount;
    }

    try {
      accountStore.setUpdating(true);
      accountStore.setRefreshing(true);
      const response = await musicService.getUserAccount();

      if (response?.code === 401) {
        const syncedLocalUser = await syncLocalBackendClerkUser()
        if (syncedLocalUser) {
          const retryResponse = await musicService.getUserAccount()
          if (retryResponse?.data) {
            backendAccountUnauthorizedUntil = 0;
            accountStore.setUserAccount(retryResponse.data);
            saveUserAccountToCookie(retryResponse.data);
            return retryResponse.data;
          }
        }

        console.warn("fetchUserAccount: Backend returned 401. Keeping Clerk session and clearing only backend account state.");
        backendAccountUnauthorizedUntil = Date.now() + BACKEND_ACCOUNT_401_BACKOFF;
        accountStore.clearUserAccount();
        saveUserAccountToCookie(null);
        return null;
      }

      if (response?.data) {
        backendAccountUnauthorizedUntil = 0;
        accountStore.setUserAccount(response.data);
        saveUserAccountToCookie(response.data);
        return response.data;
      }
      
      // Handle cases where response is okay, but data is explicitly null (e.g., new user)
      if (response && response.hasOwnProperty('data') && response.data === null) {
        console.log("fetchUserAccount: Backend returned data as null. Clearing local account.");
        backendAccountUnauthorizedUntil = 0;
        accountStore.clearUserAccount();
        saveUserAccountToCookie(null);
        return null;
      }

      // For other non-401, no-data scenarios (malformed response, unexpected structure)
      console.warn("fetchUserAccount: No valid account data from backend (not 401, not explicit data:null). Response:", response);
      // Keep existing account data to be safe from transient API errors wiping local state.
      return accountStore.userAccount; 

    } catch (error) {
      console.error("fetchUserAccount: Error during API call to musicService.getUserAccount():", error);
      // Keep existing account on general errors
      return accountStore.userAccount;
    } finally {
      accountStore.setLoadingAccount(false);
      accountStore.setUpdating(false);
      accountStore.setRefreshing(false);
    }
  };

  // 等待token并获取账户信息的公共函数
  const waitForTokenAndFetchAccount = (source = "未知") => {
    console.log(`waitForTokenAndFetchAccount called from: ${source}`);
    if (isAuthenticated.value) {
        fetchUserAccount();
    } else {
        // If not signed in, no point waiting for token. Watcher will handle sign-in.
        console.log("waitForTokenAndFetchAccount: Not signed in, will not wait for token.");
    }
  };

  // 全局账户信息刷新函数
  const refreshUserAccount = async (force = false) => {
    const now = Date.now();

    // 如果不是强制刷新，检查是否需要刷新
    if (!force && now - lastRefreshTime < MIN_REFRESH_INTERVAL) {
      return;
    }

    // 如果正在更新中，跳过
    if (accountStore.isUpdating && !force) {
      console.log("refreshUserAccount: Skipped, update in progress and not forced.");
      return;
    }
    // fetchUserAccount will check isSignedIn and manage token itself.
    if(isAuthenticated.value) {
        try {
            await fetchUserAccount();
            lastRefreshTime = now;
        } catch (error) {
            console.error("refreshUserAccount: Error during fetchUserAccount:", error);
        }
    } else {
        // console.log("refreshUserAccount: Skipped, user not signed in (Clerk).")
    }
  };

  // 启动全局定时刷新
  const startGlobalRefresh = () => {
    if (globalRefreshTimer) return;
    console.log("Starting global refresh timer.");
    globalRefreshTimer = setInterval(() => {
      if (isAuthenticated.value) { // isAuthenticated is now isSignedIn.value
        refreshUserAccount();
      }
    }, REFRESH_INTERVAL);
  };

  // 停止全局定时刷新
  const stopGlobalRefresh = () => {
    if (globalRefreshTimer) {
      console.log("Stopping global refresh timer.");
      clearInterval(globalRefreshTimer);
      globalRefreshTimer = null;
    }
  };

  // 保存用户账户信息到cookie，7天过期，用于插件获取用户信息
  const saveUserAccountToCookie = (account: UserAccount | null) => {
    if (account) {
      document.cookie = `user_account=${JSON.stringify(
        account
      )}; path=/; secure; samesite=strict; max-age=604800`; // 7天过期
    } else {
      // 清除 cookie
      document.cookie =
        "user_account=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
    }
  };

  // 修改初始化函数
  const initAuthStateWatcher = () => {
    if (accountStore.isInitialized) return;
    console.log("initAuthStateWatcher: Initializing auth state - client side.");

    // Try to load from localStorage first for quick UI, but verify with Clerk state.
    const cachedAccountData = accountStore.loadCachedAccount(); // this just sets store.userAccount
    
    watch([isAuthReady, isAuthenticated], ([ready, isClerkSignedIn]) => {
      console.log(`initAuthStateWatcher: Clerk ready=${ready}, authenticated=${isClerkSignedIn}`);
      if (!ready) {
        return;
      }

      if (isClerkSignedIn) {
        console.log("Authenticated via Clerk & session loaded. Fetching account & starting refresh.");
        fetchUserAccount().then(() => startGlobalRefresh());
      } else {
        console.log("De-authenticated via Clerk. Clearing local auth data and stopping refresh.");
        authService.clearToken();
        accountStore.clearUserAccount();
        saveUserAccountToCookie(null);
        stopGlobalRefresh();
      }
    }, { immediate: false });

    // Initial state check
    if (!isAuthReady.value) {
      console.log("initAuthStateWatcher: Clerk auth state is still loading. Keeping cached local data until auth resolves.");
    } else if (isAuthenticated.value) { // Clerk is signed in at init
      console.log("initAuthStateWatcher: Initially authenticated (Clerk).");
      fetchUserAccount().then(() => startGlobalRefresh());
    } else { // Clerk is NOT signed in at init
      console.log("initAuthStateWatcher: Initially NOT authenticated (Clerk). Clearing any stale local data.");
      // If cached data was loaded but Clerk says not signed in, clear it.
      if (accountStore.userAccount || authService.getToken()) {
        authService.clearToken();
        accountStore.clearUserAccount();
        saveUserAccountToCookie(null);
      }
    }
    accountStore.setInitialized(true);
  };

  // 获取临时用户id
  const getTempUserId = () => {
    const tempUserId = localStorage.getItem("temp_user_id");
    console.log("tempUserId", tempUserId);
    if (tempUserId) {
      return tempUserId;
    }
    return null;
  };

  // 设置临时用户id
  const setTempUserId = (userId: string) => {
    localStorage.setItem("temp_user_id", userId);
  };

  // 修改 onMounted 逻辑
  onMounted(() => {
    console.log("客户端挂载完成，检查初始化状态");

    // 只在客户端且未初始化时执行初始化逻辑
    if (typeof window !== 'undefined' && !accountStore.isInitialized) {
      console.log("onMounted: Client mounted, initializing auth state watcher.");
      initAuthStateWatcher();
    }
  });

  // 从store获取响应式状态，确保所有组件共享相同的状态
  return {
    isAuthenticated,
    isLoggingIn: computed(() => accountStore.isLoggingIn),
    handleSignIn,
    fetchAndStoreToken, // Expose if needed externally, but primarily used internally
    handleLogout,
    user, // from useUser()
    userAccount: computed(() => accountStore.userAccount),
    isLoadingAccount: computed(() => accountStore.isLoadingAccount),
    isRefreshing: computed(() => accountStore.isRefreshing), // 新增
    totalPoints: computed(() => accountStore.totalPoints),
    refreshUserAccount, // 导出刷新方法
    waitForTokenAndFetchAccount, // Review if this is still needed with new flow
    getTempUserId,
    setTempUserId,
  };
} 
